// home/stakes.ts (CX5) - the sit-down CHOICE: which stakes you sit at, and how deep you buy in.
//
// THE LOAD-BEARING IDEA (CX5 objective, Joe-approved 2026-07-17): stakes change the READS, not just the
// numbers. A $1/$3 live game is loose-passive - whales, calling stations, promo-chasers, wide ranges, few
// re-raises. A $3/$5 game is a step tougher - fewer whales, more competent regulars who understand position
// and pressure. That difference is HONEST here because it rides the shipped engine data, not a cosmetic
// bigger-number swap: each archetype carries its OWN preflop `rangeSetId` (live_loose = wide / baseline /
// live_tight = tight) and its OWN `observance` (how well it reads and adjusts). So changing WHICH archetypes
// sit down automatically changes the ranges the table plays AND how tough the pool is to read. The roster is
// the lever; the felt dollars and the ranges follow from the stakes + the mix.
//
// STAKES-AGNOSTIC BY CONSTRUCTION (CX5 -> CX8): everything here is config. Adding $5/$10 (CX8) is one more
// entry in STAKES_POOLS + STAKES_ORDER - a data add, never a rebuild. The `Stakes` values mirror the shipped
// `packages/engine/src/data/stakes/stakes.v1.json` ladder (that JSON is the engine-side source of truth for
// the same presets; this file is the app-side session/roster mapping the engine cannot own, because the
// roster is a session concern the engine takes as an explicit input).
//
// Sources: the two rosters + table-type labels are the Caribbean Kirkland field guide's $1/$3-vs-$3/$5 split
// (field guide §2, §12, §26). $1/$3 = loose-passive (§12.1); $3/$5 = the graduate game, more regular-heavy
// and aggressive (§2, §26), mapped to the `aggressiveReg` table type (§12.5). The archetype range families +
// observance are D1/D2 data (packages/engine/src/data/archetypes/*).
import type { ArchetypeId, Stakes, TableType } from '@postflop/schema';

/** The shipped cash stakes levels. `5_10` (CX8) is the third, reg-heavy level - one more entry, no rebuild. */
export type StakesId = '1_3' | '3_5' | '5_10';

export interface StakesPool {
  id: StakesId;
  /** display label for the picker + crumb, e.g. '$1 / $3'. */
  label: string;
  /** drops straight into the session `stakes` (and therefore every real-dollar amount on the felt). */
  stakes: Stakes;
  /** the read-the-table (Axis B) ground-truth label for this pool's character. */
  tableType: TableType;
  /**
   * Villains in seat order. THE FOUR TAGGABLE ARCHETYPES LEAD (rock, sticker=Vacuum, firestarter=Firecracker,
   * tourist=Whale) so the read UI always has scoreable ground truth for all four tags, even at a 6-handed
   * table where `rosterFor` slices to the first five. The fill after them is the honest pool difference.
   */
  roster: ArchetypeId[];
  /** short two-word caption under the label in the picker (e.g. 'Loose · passive'). Data-driven so a third
   *  level (CX8) reads distinctly instead of collapsing into a shared hardcoded string. */
  caption: string;
  /** one-line honest character note shown under the picker. */
  note: string;
}

/**
 * $1/$3 - the loose-passive pool. Leads with the four taggable types, then fills whale/station/promo-heavy:
 * sheriff (loose call-station-ish), promoChaser (the signature $1/$3 jackpot chaser), and only two regs
 * (bully, tableCaptain). Range families skew live_loose; average observance is low (tourist/promoChaser/
 * sticker all read poorly), which is what makes the pool soft.
 *
 * The order is EXACTLY the roster this table shipped with before CX5 (the four taggable, then sheriff, bully,
 * promoChaser, tableCaptain), so the default $1/$3 table is byte-identical to the pre-CX5 one - the deals,
 * the seeded fixtures (full.test.ts), and Joe's screenshots all still hold. CX5 only ADDS the $3/$5 pool; it
 * does not disturb the $1/$3 game.
 */
const POOL_1_3: StakesPool = {
  id: '1_3',
  label: '$1 / $3',
  stakes: { sb: 1, bb: 3, currency: 'USD' },
  tableType: 'loosePassive',
  roster: ['sticker', 'firestarter', 'rock', 'tourist', 'sheriff', 'bully', 'promoChaser', 'tableCaptain'],
  caption: 'Loose · passive',
  note: 'Loose and passive. Whales, calling stations, and promo-chasers, with a couple of regs. Wide ranges, few re-raises.',
};

/**
 * $3/$5 - a step tougher. Same four taggable leads (so the Whale/Vacuum tags still have a - rarer - correct
 * answer), but the fill is competent regulars: surgeon (obs 0.9), taxCollector (0.7), bully (0.6), and a
 * tight waitingRoom nit. Range families skew baseline/tight and average observance is markedly higher, so
 * the pool is genuinely harder to read and plays back at you. Kept exactly ONE whale (tourist) + ONE station
 * (sticker) on purpose - dropping them entirely would break read-training for those two tags at $3/$5.
 */
const POOL_3_5: StakesPool = {
  id: '3_5',
  label: '$3 / $5',
  stakes: { sb: 3, bb: 5, currency: 'USD' },
  tableType: 'aggressiveReg',
  roster: ['rock', 'firestarter', 'sticker', 'tourist', 'surgeon', 'taxCollector', 'bully', 'waitingRoom'],
  caption: 'Tougher regs',
  note: 'A step up. Fewer whales, more competent regulars who play position and pressure. Tighter and more aggressive.',
};

/**
 * $5/$10 (CX8) - the tightest, reg-heavy pool: competent regulars, minimal whales. Same four taggable leads
 * (so Whale/Vacuum/Firecracker/Rock still score), and the same deliberate ONE whale (tourist) + ONE station
 * (sticker) kept so those two tags keep a - now rare - correct answer. The step up from $3/$5 is HONEST and
 * observance-driven, not cosmetic: the fill swaps $3/$5's passive nit (waitingRoom, obs 0.3) for a sharp
 * aggressive reg (tableCaptain, obs 0.6), lifting the table's average observance 0.41 -> 0.44. The pool reads
 * you back harder - which is what actually changes at $5/$10, not the size of the numbers.
 *
 * tableType = 'tight' (field guide §12.4): the North Star names $5/$10 "the tightest of the live table-type
 * families," and a distinct Axis-B label from $3/$5's 'aggressiveReg' is itself read-training - you learn to
 * tell a tight reg game from a loose-aggressive one. (Approved by Joe 2026-07-18, mapping shown before build.)
 *
 * COST-FREE library coverage: every archetype here is already in the baked CX7 library (union of the $1/$3 and
 * $3/$5 rosters), so $5/$10 inherits the quality Haiku prose for free - matching buckets serve the baked prose
 * offline at $0, any gap falls to the $0 template. No paid re-bake needed (Joe: config-only, 2026-07-18).
 */
const POOL_5_10: StakesPool = {
  id: '5_10',
  label: '$5 / $10',
  stakes: { sb: 5, bb: 10, currency: 'USD' },
  tableType: 'tight',
  roster: ['rock', 'firestarter', 'sticker', 'tourist', 'surgeon', 'taxCollector', 'tableCaptain', 'bully'],
  caption: 'Toughest · tight',
  note: 'The toughest room. Competent regulars who play position and pressure, with only a lone whale and station left. Tight ranges, sharp reads, few free pots.',
};

export const STAKES_POOLS: Record<StakesId, StakesPool> = { '1_3': POOL_1_3, '3_5': POOL_3_5, '5_10': POOL_5_10 };
/** Display order in the picker. CX8 appends '5_10' - a data add, exactly as the module header promised. */
export const STAKES_ORDER: readonly StakesId[] = ['1_3', '3_5', '5_10'];
export const DEFAULT_STAKES_ID: StakesId = '1_3';

/** A buy-in depth you can sit down with, in big blinds - the same ladder for every stakes level. Real dollars
 *  scale with the stakes you pick (100bb = $300 at $1/$3, $500 at $3/$5), which is the point of showing it. */
export interface BuyInDepth {
  bb: number;
  /** short label, e.g. 'Standard'. */
  label: string;
}
export const BUY_IN_DEPTHS: readonly BuyInDepth[] = [
  { bb: 75, label: 'Short' },
  { bb: 100, label: 'Standard' },
  { bb: 200, label: 'Deep' },
];
export const DEFAULT_BUY_IN_BB = 100;

/** The buy-in in real dollars for a stakes level + depth: bb * depth. Feeds the session `buyIn`, from which
 *  `specForHand` derives `stackDepthBB = buyIn / bb`, closing the loop back to the depth you chose. */
export const buyInFor = (id: StakesId, depthBB: number): number => STAKES_POOLS[id].stakes.bb * depthBB;

/** The AppShell crumb line for a chosen stakes level, e.g. 'Local . $1 / $3'. */
export const stakesCrumb = (id: StakesId): string => `Local . ${STAKES_POOLS[id].label}`;

/** Format whole dollars for the picker, e.g. 300 -> '$300', 1000 -> '$1,000'. */
export const dollars = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;
